import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetSet, useSessionStorage } from 'react-use';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import EF from '@everflow/everflow-sdk';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import { nanoid } from 'nanoid';

import {
  useGetStatesWhereAshIsAvailableQuery,
  useLazyGetCampaignInfoQuery,
  useSendOrchestrateFormMutation
} from 'services/auth/auth';
import { useLazyGetMembershipPlansQuery } from 'services/lookup/lookup';

import { selectOrchestrate, selectSignUp, selectStatesWhereAshIsAvailable } from 'store';
import { OrchestrateState } from 'store/orchestrate-signup/orchestrate.types';
import { setMif, setMifQA, setWholeState } from 'store/orchestrate-signup/orchestrateSlice';
import { PlanOverrideProps, SignUpProps, ValidCouponCodes } from 'store/signup/signup.types';
import {
  setMembershipPlans,
  setPromoVisited,
  setSelectedPricePoint,
  setSignupOptions,
  setUserState
} from 'store/signup/signupSlice';

import {
  checkStepsList,
  getPlanByUrlParam,
  handleUrl,
  isForbiddenStepForNotSignedUp,
  isForbiddenStepForSignedUp,
  parseSignupSession,
  setPartnerData
} from 'containers/SignUp/signup.settings';
import { MoveStepFunction } from 'containers/SignUp/signUp.types';

import {
  BENEFITS_CHECK_TT_FLOW_PART,
  BENEFITS_CHECK_WM_FLOW_PART,
  FLOW_TYPES,
  WM_FLOWS
} from 'constants/onboarding';
import { DEFAULT_UTI_PRICE_POINT } from 'constants/pricepoints';
import { useAppDispatch, useAppSelector, useQuery } from 'hooks';
import { SIGNUP_COUPON_CODES } from 'utils/constants';
import { FlowTypes, PathName, PlanCodes, PlanTypes, SignUpSteps } from 'utils/enums';
import {
  getPartnerPricePoint,
  getPricePoint,
  handleRequestCatch,
  overridePlans
} from 'utils/helpers';

import { MembershipPlan } from 'models/plans.types';

import useClearSignup from './useClearSignup';

const flowQueryParams = [
  'v',
  'cp',
  'promo',
  'shouldEmulateBack',
  'restoreToken',
  'partnerToken',
  'hideProgress',
  'hideHeader',
  's',
  'step',
  'subStep',
  'a'
];

export const useOnboarding = () => {
  const hasRun = useRef(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const query = useQuery();

  const {
    plan = PlanTypes.WeightManagement,
    flow = FlowTypes.WeightManagementFlow,
    stepName = SignUpSteps.State
  } = useParams<{ flow: FlowTypes; plan: PlanTypes; stepName: SignUpSteps }>();
  const [searchParams] = useSearchParams();

  const [isBackAnimation, toggleBackAnimation] = useGetSet(false);

  const statesWithAsh = useAppSelector(selectStatesWhereAshIsAvailable);

  const { user, signupOptions } = useAppSelector(selectSignUp);
  const {
    mif_qa: mifQA,
    user: orchUser,
    payment: { product_price_point_handle: productPricePointHandle }
  } = useAppSelector(selectOrchestrate);

  const insuranceAnswer = mifQA.find((a) => a.question === 'insurance_types')?.answer ?? [];
  const shouldUseInsurance =
    String(mifQA.find((a) => a.question === 'use_insurance')?.answer) ?? '';
  const bmi = mifQA.find((q) => q.question === 'bmi_value')?.answer ?? 0;

  const restoreToken = query.get('restoreToken') ?? '';
  const partnerToken = query.get('partnerToken') ?? '';
  const promoVisitedCode = (query.get('promo') || '').toLowerCase() as ValidCouponCodes;
  const predefinedCategory = query.get('c');
  const campaignCode = query.get('cp') || signupOptions?.campaignId;
  const flowVer = query.get('v');

  const clearSignupStore = useClearSignup();

  const getFlowType = () => {
    if (flow === FlowTypes.TripleTherapy) {
      return 'tt';
    }
    if (WM_FLOWS.includes(flow)) {
      return 'wm';
    }
    return null;
  };

  useGetStatesWhereAshIsAvailableQuery(
    { planType: getFlowType() || 'wm' },
    {
      skip: !getFlowType()
    }
  );
  const [sendData, { isLoading }] = useSendOrchestrateFormMutation();
  const [getCampaignInfo] = useLazyGetCampaignInfoQuery();
  const [getMembershipPlans, { isLoading: isGettingPlans }] = useLazyGetMembershipPlansQuery();

  const [steps, setSteps] = useGetSet<SignUpSteps[]>([]);
  const [currentStep, setCurrentStep] = useState(-1);

  const [onboardingId, setOnboardingId] = useSessionStorage('onboarding-id');

  const moveToStep: MoveStepFunction = (
    step: number | 'next' | 'prev',
    options = {
      programNavigate: false,
      searchParams: '',
      updatedPlan: undefined
    }
  ) => {
    const { searchParams, updatedPlan, programNavigate } = options;
    if (updatedPlan) {
      dispatch(setSignupOptions({ plan: updatedPlan }));
    }
    const p = updatedPlan || plan;
    if (typeof step === 'number' && step < 0) {
      return navigate({ pathname: PathName.Login, search: searchParams });
    }
    if (step === 'next') {
      toggleBackAnimation(false);
      setCurrentStep(currentStep + 1);
      navigate({
        pathname: `${PathName.SignUp}/${p}/${flow}/${steps()[currentStep + 1]}`,
        search: searchParams
      });
    } else if (step === 'prev') {
      toggleBackAnimation(true);
      const isRestoredSession = sessionStorage.getItem('restoredSession') === 'true';
      if (isRestoredSession) {
        setCurrentStep(currentStep - 1);
        if (currentStep - 1 < 0) {
          navigate(PathName.Login);
        } else {
          navigate({
            pathname: `${PathName.SignUp}/${p}/${flow}/${steps()[currentStep - 1]}`,
            search: searchParams
          });
        }
      } else {
        navigate(-1);
      }
    } else {
      if (programNavigate) {
        toggleBackAnimation(true);
        return navigate(-1);
      }
      toggleBackAnimation(step < currentStep);
      setCurrentStep(step);
      navigate({
        pathname: `${PathName.SignUp}/${p}/${flow}/${steps()[step]}`,
        search: searchParams
      });
    }
  };

  const onInit = () => {
    // TODO rewrite it by moving useOnboarding higher or wrap with the context to avoid additional calls
    if (hasRun.current) return; // Prevents running more than once
    hasRun.current = true;

    const previousTransactionId = EF.getTransactionId(EF.urlParameter('oid'));
    if (!previousTransactionId) {
      EF.click({
        affiliate_id: 514,
        order_id: EF.urlParameter('oid')
      });
    }

    if (!onboardingId) {
      setOnboardingId(nanoid());
    }
    const hasActiveSession = parseSignupSession();
    if (!hasActiveSession) {
      clearSignupStore();
    }
    let arr: {
      answer: string;
      question: string;
    }[] = [];
    searchParams.forEach((answer, question) => {
      arr.push({ answer, question });
    });
    arr = arr.filter((a) => !flowQueryParams.includes(a.question));
    if (arr.length > 0) {
      sessionStorage.setItem('restoredSession', 'true');
      dispatch(setMifQA(arr));
      const questionsList = arr.map((a) => a.question);
      if (
        questionsList.includes('weight') &&
        questionsList.includes('height') &&
        questionsList.includes('bmi_value')
      ) {
        const currentDate = dayjs().format('YYYY-MM-DD');
        dispatch(
          setMif({
            bmi: {
              date: currentDate,
              value: Number(arr.find((a) => a.question === 'bmi_value')?.answer)
            },
            height: {
              date: currentDate,
              value: Number(arr.find((a) => a.question === 'height')?.answer)
            },
            weight: {
              date: currentDate,
              value: Number(arr.find((a) => a.question === 'weight')?.answer)
            }
          })
        );
      }
    }
    document.querySelector('meta[name="apple-itunes-app"]')?.remove();
    const options: Partial<SignUpProps['signupOptions']> = {};
    if (predefinedCategory) {
      options.predefinedCategory = predefinedCategory;
    }
    if (flowVer) {
      options.flowVersion = flowVer;
    }
    if (!signupOptions?.flow) {
      options.flow = flow;
    }
    if (!signupOptions?.plan) {
      options.plan = plan;
    }

    if (!!Object.keys(options).length) {
      dispatch(setSignupOptions(options));
    }
    // logic for params validity - if 1 of params is invalid, we should redirect user to the default flow 1-st step
    const { isValidParams, patchedPlan, patchedFlow, patchedStepName } = handleUrl(
      plan,
      flow,
      stepName,
      (!hasActiveSession &&
        !predefinedCategory &&
        ![SignUpSteps.InitialInfo, SignUpSteps.WMWeightLoseAim, SignUpSteps.WMBasicInfo].includes(
          stepName
        )) ||
        (!!signupOptions?.flow && signupOptions?.flow !== flow) ||
        (!!signupOptions?.plan && signupOptions?.plan !== plan)
    );

    if (!isValidParams) {
      clearSignupStore();
      navigate(
        {
          pathname: `${PathName.SignUp}/${patchedPlan}/${patchedFlow}/${patchedStepName}`
        },
        {
          replace: true
        }
      );
    }
    if (campaignCode) {
      getCampaignInfo(campaignCode)
        .unwrap()
        .then(({ data }) => {
          if (data?.settings) {
            dispatch(setSignupOptions({ campaignId: campaignCode }));
            getMembershipPlans()
              .unwrap()
              .then((plansData) => {
                getMembershipPlansThen(plansData.data, data.settings?.plans);
              })
              .catch(getMembershipPlansCatch);
          } else {
            getMembershipPlans()
              .unwrap()
              .then((r) => getMembershipPlansThen(r.data))
              .catch(getMembershipPlansCatch);
          }
        });
    } else {
      getMembershipPlans()
        .unwrap()
        .then(({ data }) => getMembershipPlansThen(data))
        .catch(getMembershipPlansCatch);
    }
    if (WM_FLOWS.includes(patchedFlow)) {
      handleAdditionalTokens(patchedFlow);
    } else {
      setSteps(FLOW_TYPES[patchedFlow]);
    }
  };

  const getMembershipPlansThen = (plans: MembershipPlan[], overrides?: PlanOverrideProps[]) => {
    let overriddenPlans = plans;
    if (!!overrides?.length && campaignCode) {
      overriddenPlans = overridePlans(plans, overrides);
    }
    dispatch(setMembershipPlans(overriddenPlans));

    const { patchedPlan } = handleUrl(plan, flow, stepName, false);
    const selectedPlan = getPlanByUrlParam(patchedPlan, overriddenPlans);
    if (!selectedPlan && patchedPlan !== PlanTypes.NotSelected) {
      throw new Error('Error with getting plan details, please try later');
    }
    if (selectedPlan && patchedPlan !== PlanTypes.NotSelected) {
      dispatch(
        setUserState({
          // need to add/remove below ...user to actually clear data. For now I keep it for dev purposes
          planCode: selectedPlan.planCode,
          planId: selectedPlan._id
        })
      );
      const qty =
        selectedPlan.planCode === PlanCodes.WeightManagementMembership ||
        (!!overrides?.length && campaignCode)
          ? undefined
          : flow === FlowTypes.UTIFlow
            ? 1
            : selectedPlan.planCode === PlanCodes.TotalCareMembership
              ? 3
              : 1;

      const partnerPricePoint = getPartnerPricePoint(flow);
      const pricePoint = getPricePoint(
        selectedPlan.pricePoints,
        qty,
        flow === FlowTypes.UTIFlow && selectedPlan.planCode === PlanCodes.FlexCare
          ? DEFAULT_UTI_PRICE_POINT
          : partnerPricePoint
      );
      pricePoint && dispatch(setSelectedPricePoint(pricePoint));
    }
    if (SIGNUP_COUPON_CODES.hasOwnProperty(promoVisitedCode)) {
      dispatch(setPromoVisited(promoVisitedCode));
    } else if (promoVisitedCode && !SIGNUP_COUPON_CODES.hasOwnProperty(promoVisitedCode)) {
      dispatch(setPromoVisited(''));
    }
  };

  const handleStepsListUpdateOnDynamicParamsChanged = () => {
    const { shouldUpdateSteps, newStepsList } = checkStepsList(
      {
        BMI: Number(bmi),
        isAshAllowed: statesWithAsh?.includes(orchUser.state),
        pricePointID: productPricePointHandle,
        sexAtBirth: user.sexAtBirth,
        state: orchUser.state
      },
      steps(),
      Array.isArray(insuranceAnswer) ? insuranceAnswer : [],
      flow
    );
    if (shouldUpdateSteps) {
      setSteps(newStepsList);
    }
  };

  const getMembershipPlansCatch = (e: MessageEvent) => {
    handleRequestCatch(e, 'Unexpected error with getting plan details');
  };

  const handleStepUpdate = () => {
    const isCompletingSignup = query.has('finishSignupToken');

    if (steps().length) {
      if (isForbiddenStepForNotSignedUp(user, flow, stepName) && !isCompletingSignup) {
        moveToStep(0);
      } else if (
        isForbiddenStepForSignedUp(
          {
            completedWMOnboarding: !!user.accessToken,
            hasSubscription: !!user.hasSubscription,
            status: user.status
          },
          flow,
          stepName
        )
      ) {
        if (WM_FLOWS.includes(flow) && stepName !== SignUpSteps.WMCompleteOnboarding) {
          const completeOnboardingStep = steps().indexOf(SignUpSteps.WMCompleteOnboarding);
          if (completeOnboardingStep !== -1) {
            moveToStep(completeOnboardingStep);
          }
        }
        const preConfirmationStep = steps().indexOf(SignUpSteps.PreConfirmation);
        const createUserStep = steps().indexOf(SignUpSteps.CreateNewUser);
        moveToStep(
          [FlowTypes.ShortFlow, FlowTypes.ShortFlowWithOptionalPopup].includes(flow)
            ? createUserStep
            : preConfirmationStep
        );
      } else if (steps().indexOf(stepName) !== -1) {
        toggleBackAnimation(currentStep === steps().indexOf(stepName) + 1);
        setCurrentStep(steps().indexOf(stepName));
      } else {
        moveToStep(0);
      }
    }
  };

  const handleAdditionalTokens = (patchedFlow: FlowTypes) => {
    const getBody = (): Partial<OrchestrateState> | null => {
      if (restoreToken) {
        return {
          restoreToken
        };
      }
      if (partnerToken) {
        const parsed = jwtDecode(partnerToken) as {
          clientEmail?: string;
          clientMedId?: string;
          clientName?: string;
          coachEmail?: string;
          coachId?: string;
          coachName?: string;
          iat?: number;
          sub?: string;
        };
        const userModel = { ...orchUser };
        if (parsed?.clientName) {
          userModel.firstName = parsed.clientName?.split(' ')?.[0] ?? '';
          userModel.lastName = parsed.clientName?.split(' ')?.[1] ?? '';
        }
        if (parsed?.clientEmail) {
          userModel.email = parsed.clientEmail;
        }
        userModel.companyPartners = {
          connectedPartners: [],
          onboardingPartner: {
            partnerName: 'Optavia',
            ...parsed,
            extraPartnerData: parsed,
            token: partnerToken,
            tokenIsValid: true
          }
        };
        return {
          user: userModel
        };
      }
      return !!orchUser.companyPartners?.onboardingPartner?.partnerName ||
        ![FlowTypes.BlueLineFlow, FlowTypes.WeightManagementFlowOptavia].includes(patchedFlow)
        ? null
        : {
            user: {
              ...orchUser,
              ...setPartnerData(patchedFlow)
            }
          };
    };

    const obj = getBody();
    if (!!obj) {
      sendData(obj)
        .unwrap()
        .then((r) => {
          const result = r.data;
          // if this is regular response with no tokens
          if (!('refreshToken' in result)) {
            dispatch(setWholeState(result));
          }
          // TODO: for now it brings us on previous step even during regular page update. Need to think how to fix it
          // if ('session' in result) {
          //   if (result.session?.metadata.urlQuery) {
          //     sessionStorage.setItem('restoredSession', 'true');
          //     navigate(result.session?.metadata.urlQuery, { replace: true });
          //   }
          // }
        })
        .finally(() => {
          setSteps(FLOW_TYPES[patchedFlow]);
        });
    } else {
      if (
        patchedFlow === FlowTypes.WeightManagementBenefitsCheckNew &&
        !!signupOptions.flowVersion
      ) {
        const additionalSteps =
          signupOptions.flowVersion === 'glp-1'
            ? BENEFITS_CHECK_WM_FLOW_PART
            : BENEFITS_CHECK_TT_FLOW_PART;
        setSteps([...FLOW_TYPES[patchedFlow], ...additionalSteps]);
      } else {
        setSteps(FLOW_TYPES[patchedFlow]);
      }
    }
  };

  useEffect(onInit, []);
  useEffect(handleStepUpdate, [steps(), stepName]);

  useEffect(handleStepsListUpdateOnDynamicParamsChanged, [
    flow,
    statesWithAsh?.length,
    user.sexAtBirth,
    bmi,
    orchUser.state,
    orchUser.accessToken,
    steps().length,
    productPricePointHandle,
    insuranceAnswer,
    shouldUseInsurance
  ]);

  return {
    currentStep,
    flow,
    isBackAnimation: isBackAnimation(),
    isLoading: isLoading || isGettingPlans,
    moveToStep,
    setSteps,
    stepName,
    steps: steps()
  };
};
